/**
 * Web Storage Implementation
 * LocalStorage voor op het web (want daar hebben we geen AsyncStorage)
 */

import type {StorageService} from './StorageService';

class WebStorageService implements StorageService {
  private isAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('WebStorage: localStorage is niet beschikbaar:', error);
      return false;
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      if (!this.isAvailable()) {
        throw new Error('localStorage is niet beschikbaar');
      }
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`WebStorage: Fout bij ophalen item "${key}":`, error);
      throw new Error(`Kon item niet ophalen: ${key}`);
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      if (!this.isAvailable()) {
        throw new Error('localStorage is niet beschikbaar');
      }
      localStorage.setItem(key, value);
    } catch (error) {
      // Error afhandelen als het vol zit (te veel troep bewaard)
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.error('WebStorage: Opslag zit vol');
        throw new Error('Opslag zit vol');
      }
      console.error(`WebStorage: Fout bij opslaan item "${key}":`, error);
      throw new Error(`Kon item niet opslaan: ${key}`);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      if (!this.isAvailable()) {
        throw new Error('localStorage is niet beschikbaar');
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`WebStorage: Fout bij verwijderen item "${key}":`, error);
      throw new Error(`Kon item niet verwijderen: ${key}`);
    }
  }

  async clear(): Promise<void> {
    try {
      if (!this.isAvailable()) {
        throw new Error('localStorage is niet beschikbaar');
      }
      localStorage.clear();
    } catch (error) {
      console.error('WebStorage: Fout bij legen opslag:', error);
      throw new Error('Kon opslag niet legen');
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      if (!this.isAvailable()) {
        throw new Error('localStorage is niet beschikbaar');
      }
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== null) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('WebStorage: Fout bij ophalen alle sleutels:', error);
      throw new Error('Kon sleutels niet ophalen');
    }
  }
}

export const storageService: StorageService = new WebStorageService();
